import { CUSTOMERS_AHEAD_BEHIND_FIXED_TARGET_RATE } from "constants/customers";
import { CUSTOMERS_AHEAD_BEHIND_FIXED_TARGET_RATE_EXCLUDE_NPS } from "constants/customers";
import { CUSTOMERS_AHEAD_BEHIND_FIXED_TARGET_RATE_EXCLUDE_PLANNED } from "constants/customers";
import { CUSTOMERS_AHEAD_BEHIND_FIXED_TARGET_RATE_EXCLUDE_UNPLANNED } from "constants/customers";
import { DataDumpAggregateType } from "types/types";
import {
  useTrendChartBinSize,
  useVisibleProcessIds,
  useWorkScheduleIdOnCurrentPage,
} from "utils/customHooks/genericHooks";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import { ONE_HOUR_OF_MS } from "utils/timeUtils";
import { round } from "lodash";
import { useMetrics } from "components/Dashboard/GroupMetrics/useMetrics";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import { PageNameEnum } from "types/enums";
import { GroupMetricType } from "components/DataGetter/FlightConfig/FlightConfigTypes/GroupMetricType";
import { useCallback } from "react";

export const AHEAD_BEHIND_TILE_HEIGHT = 248;
export const AHEAD_BEHIND_TILE_WIDTH = 285;
export const AHEAD_BEHIND_TILE_WIDTH_MOBILE = 176;
export const LEGEND_WIDTH = 136;

export const AHEAD_OF_TARGET_COLOR = "#39b54a";
export const BEHIND_TARGET_COLOR = "#ff3333";

/** get colors based on value
 *
 * @returns { backgroundColor, textColor }
 *
 * - backgroundColor: negative = red, positive = green, null = defaultBackgroundColor | white
 * - textColor: null = black, else white
 */
export function getAheadBehindColors({
  outputLossSum,
  isFutureRange = false,
  defaultBackgroundColor = "white",
}: {
  outputLossSum: number | null;
  isFutureRange?: boolean;
  defaultBackgroundColor?: string;
}) {
  const backgroundColor =
    outputLossSum === null || isFutureRange
      ? defaultBackgroundColor
      : outputLossSum < 0
        ? BEHIND_TARGET_COLOR
        : AHEAD_OF_TARGET_COLOR;
  const textColor =
    backgroundColor === defaultBackgroundColor ? "black" : "white";

  return { backgroundColor, textColor };
}

/** difference between (scaled) target output and actual output */
export function getOutputLossSum(
  targetScale: number | undefined,
  dataDump: DataDumpAggregateType | null,
  /** override the (productionPotentialOutput * targetScale) with a fixed target */
  fixedTarget?: number
) {
  const outputCount = dataDump?.netOutput;
  const ppo = dataDump?.productionPotentialOutput;
  const outputLossSum =
    (ppo || ppo === 0) && (outputCount || outputCount === 0)
      ? outputCount - (fixedTarget || ppo * (targetScale ?? 1))
      : null;

  return outputLossSum;
}

/** custom fixed targets (only for customers in CUSTOMERS_AHEAD_BEHIND_FIXED_TARGET_RATE) */
export function useFixedTargetsOverride(
  dataDumpCurrentHour: DataDumpAggregateType | null,
  dataDumpFullRange: DataDumpAggregateType | null,
  processId: string | null = null
) {
  const ckey = useCustomerKey();
  const processIds = useVisibleProcessIds();
  // TODO: Migrate CUSTOMERS_AHEAD_BEHIND_FIXED_TARGET_RATE... settings to Flight config
  const fixedTarget =
    CUSTOMERS_AHEAD_BEHIND_FIXED_TARGET_RATE[ckey]?.[
      processId ?? processIds[0]
    ];
  const excludeNPS =
    CUSTOMERS_AHEAD_BEHIND_FIXED_TARGET_RATE_EXCLUDE_NPS[ckey]?.[
      processId ?? processIds[0]
    ];
  const excludePlanned =
    CUSTOMERS_AHEAD_BEHIND_FIXED_TARGET_RATE_EXCLUDE_PLANNED[ckey]?.[
      processId ?? processIds[0]
    ];
  const excludeUnplanned =
    CUSTOMERS_AHEAD_BEHIND_FIXED_TARGET_RATE_EXCLUDE_UNPLANNED[ckey]?.[
      processId ?? processIds[0]
    ];
  const now = Date.now();
  const { trendChartBinSizeMs: binSize } = useTrendChartBinSize();
  const hourProgressPct = dataDumpCurrentHour
    ? Math.min((now - dataDumpCurrentHour.range.ge) / binSize, 1)
    : null;
  const shiftTime = dataDumpFullRange
    ? dataDumpFullRange.range.lt - dataDumpFullRange.range.ge
    : null;
  const currentFixedTarget =
    fixedTarget && hourProgressPct ? fixedTarget * hourProgressPct : null;
  const shiftProgressPct =
    shiftTime && dataDumpFullRange
      ? Math.min((now - dataDumpFullRange.range.ge) / shiftTime, 1)
      : null;
  const hoursInShift = shiftTime ? shiftTime / binSize : 0;
  let excludedTime = 0;
  excludedTime += excludeNPS ? (dataDumpFullRange?.notScheduledTime ?? 0) : 0;
  excludedTime += excludePlanned ? (dataDumpFullRange?.plannedTime ?? 0) : 0;
  excludedTime += excludeUnplanned
    ? (dataDumpFullRange?.unplannedTime ?? 0)
    : 0;
  const productionHoursInShift =
    hoursInShift - round(excludedTime / ONE_HOUR_OF_MS, 1);
  const currentFixedShiftTarget =
    fixedTarget && shiftProgressPct
      ? fixedTarget * productionHoursInShift * shiftProgressPct
      : null;
  const totalFixedShiftTarget = fixedTarget
    ? fixedTarget * productionHoursInShift
    : null;
  return {
    currentFixedTarget,
    fixedTarget,
    currentFixedShiftTarget,
    totalFixedShiftTarget,
  };
}

export function useOutputsAndTargets(
  dataDump: DataDumpAggregateType | null,
  processId: string | null,
  currentFixedTarget?: number | null,
  totalFixedTarget?: number | null,
  isColorOnly?: boolean
) {
  const getOutputsAndTargets = useGetOutputsAndTargets(isColorOnly);
  return getOutputsAndTargets(
    dataDump,
    processId,
    currentFixedTarget,
    totalFixedTarget
  );
}

export function useGetOutputsAndTargets(isColorOnly?: boolean) {
  const [flightConfig] = useFlightConfig();
  return useCallback(
    (
      dataDump: DataDumpAggregateType | null,
      processId: string | null,
      currentFixedTarget?: number | null,
      totalFixedTarget?: number | null
    ): {
      productionPotentialOutput: number | null;
      netOutput: number | null;
      outputLossSum: number | null;
      totalPotentialOutput: number | null;
    } => {
      const targetScaleAll =
        flightConfig.general[
          isColorOnly
            ? "aheadBehindScaleTargetRateColorOnly"
            : "aheadBehindScaleTargetRate"
        ]["ALL"];
      const fallback = targetScaleAll ?? 1;
      const targetScale = processId
        ? (flightConfig.general[
            isColorOnly
              ? "aheadBehindScaleTargetRateColorOnly"
              : "aheadBehindScaleTargetRate"
          ][processId] ?? fallback)
        : fallback;

      // if undefined, then use null
      const productionPotentialOutput =
        currentFixedTarget ?? dataDump?.productionPotentialOutput ?? null;
      const totalPotentialOutput =
        totalFixedTarget ?? dataDump?.totalPotentialOutput ?? null;
      const netOutput = dataDump?.netOutput ?? null;
      const outputLossSum =
        productionPotentialOutput && (netOutput || netOutput === 0)
          ? netOutput - productionPotentialOutput * targetScale
          : null;
      const totalProductionPotentialOutput = productionPotentialOutput
        ? productionPotentialOutput * targetScale
        : null;
      const totalTargetOutput = totalPotentialOutput
        ? totalPotentialOutput * targetScale
        : null;

      return {
        netOutput,
        outputLossSum,
        productionPotentialOutput: totalProductionPotentialOutput,
        totalPotentialOutput: totalTargetOutput,
      };
    },
    [flightConfig.general, isColorOnly]
  );
}

export function useAheadBehindMetrics(
  hourDataDump: DataDumpAggregateType | null
): {
  /** metrics + value from dataDump for the shift */
  operatorPageOnlyMetrics_Shift: GroupMetricType[];
  /** metrics + value from dataDump for the hour */
  operatorPageOnlyMetrics_Hour: GroupMetricType[];
  metricsLoading: boolean;
} {
  const { metrics, loading: metricsLoading } = useMetrics(
    hourDataDump ?? undefined
  );

  const [{ metrics: metricsConfig }] = useFlightConfig();
  const workScheduleId = useWorkScheduleIdOnCurrentPage();
  const operatorPageOnlyMetricsConfig =
    metricsConfig[workScheduleId]?.filter(metric =>
      metric.onlyShowOnPages?.includes(PageNameEnum.operator)
    ) ?? [];
  let operatorPageOnlyMetrics_Shift = metrics.filter(
    metric =>
      // metric must include "operator" in onlyShowOnPages
      metric.onlyShowOnPages?.includes(PageNameEnum.operator) &&
      operatorPageOnlyMetricsConfig.some(
        config => config.dataDumpKey === metric.dataDumpKey
      )
  );

  if (operatorPageOnlyMetrics_Shift.length === 0) {
    operatorPageOnlyMetrics_Shift = DEFAULT_OPERATOR_PAGE_METRICS;
  }
  const operatorPageOnlyMetrics_Hour = operatorPageOnlyMetrics_Shift.map(
    metric => ({
      ...metric,
      value:
        metric.dataDumpKey && hourDataDump
          ? (hourDataDump[metric.dataDumpKey] as number)
          : null,
    })
  );
  return {
    operatorPageOnlyMetrics_Shift,
    operatorPageOnlyMetrics_Hour,
    metricsLoading,
  };
}

/** the scale of the ahead-behind LinearClock is designed around a default height
 * based on this number of metrics;
 * then we scale up or down based on the actual configured number of metrics */
export const DEFAULT_NUM_AB_ROWS = [
  "Hour",
  "Target Output",
  "Actual Output",
  "Ahead / Behind",
  "Current",
].length;

/** these aren't configured metrics, just labels */
export const DEFAULT_NONMETRIC_AB_ROWS = ["Hour", "Current"].length;
export const DEFAULT_METRIC_AB_ROWS =
  DEFAULT_NUM_AB_ROWS - DEFAULT_NONMETRIC_AB_ROWS;

/** default operator page metrics if no flightConfig.metrics have "onlyShowOnPages": [..., "operator"] */
const DEFAULT_OPERATOR_PAGE_METRICS: GroupMetricType[] = [
  {
    units: "",
    dataDumpKey: "totalPotentialOutput",
    displayName: "Target Output",
    onlyShowOnPages: [PageNameEnum.operator],
  },
  {
    units: "",
    dataDumpKey: "netOutput",
    displayName: "Actual Output",
    onlyShowOnPages: [PageNameEnum.operator],
  },
  {
    units: "",
    dataDumpKey: "outputLossSum",
    displayName: "Ahead / Behind",
    onlyShowOnPages: [PageNameEnum.operator],
  },
];

export function handleABSpecialMetrics({
  metric,
  target,
  count,
  aheadBehind,
  dataDump,
}: {
  metric: GroupMetricType;
  target: number | null;
  count: number | null;
  aheadBehind: number | null;
  dataDump: DataDumpAggregateType | null;
}) {
  return metric.dataDumpKey === "totalPotentialOutput"
    ? target
    : metric.dataDumpKey === "netOutput"
      ? count
      : metric.dataDumpKey === "outputLossSum"
        ? aheadBehind
        : // if we didn't find any specially-processed metrics, just use the datadump for the hour
          metric.dataDumpKey
          ? Number(dataDump?.[metric.dataDumpKey])
          : null;
}
